import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CheckSquare, FolderKanban, FileText, Sparkles, Calendar, Mic, ArrowRight, Zap, RefreshCw,
} from "lucide-react";

const PRIORITY = {
  urgent: "bg-destructive text-white",
  high: "bg-orange-500 text-white",
  medium: "bg-slate-200 text-slate-700",
  low: "bg-slate-100 text-slate-500",
};

function StatCard({ label, value, icon: Icon }) {
  return (
    <Card className="rounded-sm border-border shadow-none p-5 hover:border-slate-400 transition-colors">
      <div className="flex items-center justify-between">
        <span className="label-mono text-slate-500">{label}</span>
        <Icon size={18} className="text-slate-400" />
      </div>
      <div className="text-4xl font-black tracking-tighter mt-3">{value}</div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [recs, setRecs] = useState([]);
  const [recLoading, setRecLoading] = useState(false);

  const load = () => api.get("/dashboard").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const loadRecs = () => {
    setRecLoading(true);
    api.get("/copilot/recommendations").then((r) => setRecs(r.data)).finally(() => setRecLoading(false));
  };

  const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  };

  if (!data) return <div className="label-mono text-muted-foreground">Loading dashboard…</div>;
  const s = data.stats;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-slate-500">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-950 mt-1">
            {greeting()}, {user?.name?.split(" ")[0]}.
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => navigate("/voice")} data-testid="dash-capture-voice">
            <Mic size={16} className="mr-2" /> Capture
          </Button>
          <Button className="rounded-sm bg-ai hover:bg-blue-800" onClick={() => navigate("/copilot")} data-testid="dash-open-copilot">
            <Sparkles size={16} className="mr-2" /> Ask Copilot
          </Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard label="Open Tasks" value={s.open_tasks} icon={CheckSquare} />
        <StatCard label="Done Today" value={s.completed_today} icon={Zap} />
        <StatCard label="Projects" value={s.active_projects} icon={FolderKanban} />
        <StatCard label="Notes" value={s.notes} icon={FileText} />
        <StatCard label="Pending AI" value={s.pending_actions} icon={Sparkles} />
      </div>

      {/* AI recommendations — dark inverted card */}
      <Card className="rounded-sm bg-slate-950 text-white border-0 shadow-none p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-ai" />
            <span className="label-mono text-white/60">AI Recommendations</span>
          </div>
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/10 rounded-sm h-8"
            onClick={loadRecs} disabled={recLoading} data-testid="generate-recommendations">
            <RefreshCw size={14} className={`mr-1.5 ${recLoading ? "animate-spin" : ""}`} />
            {recLoading ? "Thinking…" : "Generate"}
          </Button>
        </div>
        {recs.length === 0 ? (
          <p className="text-white/60 text-sm">
            Let the copilot surface what matters today. Hit Generate.
          </p>
        ) : (
          <div className="grid md:grid-cols-3 gap-4" data-testid="recommendations-list">
            {recs.map((r, i) => (
              <div key={i} className="border border-white/15 rounded-sm p-4 hover:border-ai transition-colors">
                <div className="font-semibold text-sm">{r.title}</div>
                <div className="text-white/60 text-sm mt-1">{r.detail}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Priority tasks */}
        <Card className="rounded-sm border-border shadow-none p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold tracking-tight">Priority Tasks</h3>
            <button className="text-sm text-ai font-medium flex items-center gap-1" onClick={() => navigate("/tasks")} data-testid="view-all-tasks">
              View all <ArrowRight size={14} />
            </button>
          </div>
          <div className="space-y-2">
            {data.priority_tasks.length === 0 && <p className="text-sm text-muted-foreground">Nothing urgent. Enjoy the calm.</p>}
            {data.priority_tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
                <span className={`label-mono px-2 py-0.5 rounded-sm ${PRIORITY[t.priority]}`}>{t.priority}</span>
                <span className="flex-1 text-sm font-medium">{t.title}</span>
                <span className="label-mono text-slate-400">{t.status.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Upcoming + notes */}
        <div className="space-y-6">
          <Card className="rounded-sm border-border shadow-none p-5">
            <div className="flex items-center gap-2 mb-4">
              <Calendar size={16} className="text-slate-400" />
              <h3 className="text-xl font-bold tracking-tight">Upcoming</h3>
            </div>
            {data.upcoming_events.length === 0 ? (
              <p className="text-sm text-muted-foreground">No upcoming events.</p>
            ) : (
              data.upcoming_events.map((e) => (
                <div key={e.id} className="py-2 border-b border-border last:border-0">
                  <div className="text-sm font-medium">{e.title}</div>
                  <div className="label-mono text-slate-400 mt-0.5">
                    {new Date(e.start).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              ))
            )}
          </Card>

          <Card className="rounded-sm border-border shadow-none p-5">
            <div className="flex items-center gap-2 mb-4">
              <FileText size={16} className="text-slate-400" />
              <h3 className="text-xl font-bold tracking-tight">Recent Notes</h3>
            </div>
            {data.recent_notes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No notes yet.</p>
            ) : (
              data.recent_notes.map((n) => (
                <div key={n.id} className="py-2 border-b border-border last:border-0 cursor-pointer" onClick={() => navigate("/notes")}>
                  <div className="text-sm font-medium truncate">{n.title}</div>
                </div>
              ))
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
